import { Button, IconButton } from '@ads/components-react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

/** Tom visual do diálogo, controlando a cor do ícone e do botão de confirmação. */
export type ConfirmDialogTone = 'danger' | 'brand';

export interface ConfirmDialogProps {
  /** Controla a visibilidade do diálogo. */
  readonly open: boolean;
  /** Título do diálogo. */
  readonly title: string;
  /** Corpo/descrição da ação a confirmar. */
  readonly body: string;
  /** Rótulo do botão de confirmação. */
  readonly confirmLabel: string;
  /** Rótulo do botão de cancelamento. */
  readonly cancelLabel: string;
  /** Tom visual do diálogo. Padrão: `danger`. */
  readonly tone?: ConfirmDialogTone;
  /** Exibe spinner no botão de confirmação e bloqueia o cancelamento. */
  readonly isLoading?: boolean;
  /** Callback ao fechar sem confirmar (overlay, Escape, cancelar ou X). */
  readonly onClose: () => void;
  /** Callback ao confirmar a ação. */
  readonly onConfirm: () => void;
}

/**
 * Diálogo de confirmação genérico da revisão de provas, reutilizado para ações
 * destrutivas (excluir idioma) e de efeito global (finalizar tradução). Fecha ao
 * pressionar Escape ou clicar no overlay.
 *
 * @param open - Controla a visibilidade do diálogo
 * @param title - Título do diálogo
 * @param body - Corpo/descrição da ação
 * @param confirmLabel - Rótulo do botão de confirmação
 * @param cancelLabel - Rótulo do botão de cancelamento
 * @param tone - Tom visual (`danger` | `brand`)
 * @param isLoading - Exibe spinner e bloqueia o cancelamento
 * @param onClose - Callback ao fechar sem confirmar
 * @param onConfirm - Callback ao confirmar
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={open}
 *   title={t('langs.deleteModal.title')}
 *   body={t('langs.deleteModal.body', { language })}
 *   confirmLabel={t('langs.deleteModal.confirm')}
 *   cancelLabel={t('langs.deleteModal.cancel')}
 *   onClose={() => setOpen(false)}
 *   onConfirm={handleDelete}
 * />
 * ```
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  isLoading = false,
  onClose,
  onConfirm,
}: ConfirmDialogProps) {
  if (!open) return null;

  const iconWrapperClass =
    tone === 'danger'
      ? 'bg-base-danger-100 text-base-danger-700'
      : 'bg-base-accent-100 text-base-accent-700';

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay — click/keydown intentionally close the dialog
    <div
      className="fixed inset-none z-[999] flex items-center justify-center bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
      role="presentation"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        className="flex w-full max-w-[27.5rem] flex-col gap-xxxs rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level3"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div
            className={`flex h-[2.75rem] w-[2.75rem] items-center justify-center rounded-sm ${iconWrapperClass}`}
          >
            <ExclamationTriangleIcon className="h-mini w-mini" />
          </div>
          <IconButton
            icon={XMarkIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={cancelLabel}
            onClick={onClose}
          />
        </div>

        {/* Text */}
        <div className="flex flex-col gap-nano">
          <span className="ts-heading-sm text-ctx-content-title">{title}</span>
          <p className="ts-paragraph-xs leading-lg text-ctx-content-base">{body}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-micro">
          <Button hierarchy="secondary" size="md" onClick={onClose} disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            hierarchy={tone === 'danger' ? 'danger' : 'primary'}
            size="md"
            onClick={onConfirm}
            isLoading={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
